/**
 * The owner's inbox for portal messages.
 *
 * Lives in the existing admin area rather than a new app: the owner already
 * signs in here as `admin`, the auth and API base are already wired, and the
 * alternative (mission-control) talks to NocoDB, not the portal database.
 *
 * Two panes — threads on the left, the conversation and a reply box on the
 * right. The selected thread is a URL param so the deep link in the
 * notification email lands on the right conversation.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Mail, MessageSquare, Send } from 'lucide-react';
import {
  adminMessageService,
  type AdminThreadSummary,
  type ThreadMessage,
} from '../../services/messageService';

/** Inbox re-checks on this cadence so a new message shows up unprompted. */
const POLL_MS = 30_000;

function timeLabel(iso: string): string {
  const date = new Date(iso);
  const mins = Math.round((Date.now() - date.getTime()) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h ago`;
  return date.toLocaleDateString();
}

function ThreadRow({
  thread,
  active,
  onSelect,
}: {
  thread: AdminThreadSummary;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full border-b border-gray-700 px-4 py-3 text-left transition-colors ${
        active ? 'bg-gray-700' : 'hover:bg-gray-750'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate font-medium text-white">
          {thread.businessName || thread.user.name}
        </span>
        <span className="shrink-0 text-xs text-gray-500">{timeLabel(thread.lastMessageAt)}</span>
      </div>
      <p className="mt-0.5 truncate text-xs text-gray-500">{thread.user.email}</p>
      {thread.lastMessage && (
        <p className="mt-1.5 line-clamp-2 text-sm text-gray-400">
          {thread.lastMessage.sender === 'team' && (
            <span className="text-gray-500">You: </span>
          )}
          {thread.lastMessage.body}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {thread.unreadCount > 0 && (
          <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-400">
            {thread.unreadCount} new
          </span>
        )}
        {thread.awaitingReply && thread.unreadCount === 0 && (
          <span className="rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-400">
            Needs reply
          </span>
        )}
        {thread.deliveryError && (
          <span className="flex items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-xs text-red-400">
            <AlertTriangle size={11} />
            Not delivered
          </span>
        )}
      </div>
    </button>
  );
}

function MessageBubble({ message }: { message: ThreadMessage }) {
  const isTeam = message.sender === 'team';
  return (
    <li className={`flex ${isTeam ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-xl px-4 py-3 ${
          isTeam ? 'bg-emerald-500/15 text-emerald-50' : 'bg-gray-700 text-gray-100'
        }`}
      >
        <div className="mb-1 flex items-center gap-2 text-xs text-gray-400">
          <span>{isTeam ? 'You' : 'Customer'}</span>
          <span>·</span>
          <span>{timeLabel(message.createdAt)}</span>
          {message.deliveryError && (
            <span className="flex items-center gap-1 text-red-400" title={message.deliveryError}>
              <AlertTriangle size={11} />
              delivery failed
            </span>
          )}
        </div>
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.body}</p>
      </div>
    </li>
  );
}

export default function AdminMessagesPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const selectedId = searchParams.get('thread');

  const { data: inbox, isLoading } = useQuery({
    queryKey: ['admin-message-threads'],
    queryFn: adminMessageService.listThreads,
    refetchInterval: POLL_MS,
  });

  // Memoised: it is a useEffect dependency, and a fresh `[]` every render would
  // re-run the auto-select effect on every paint.
  const threads = useMemo(() => inbox?.threads ?? [], [inbox]);

  // Land on something: the email deep-links a thread, a cold open takes the top.
  useEffect(() => {
    if (selectedId || threads.length === 0) return;
    const first = threads[0];
    if (first) setSearchParams({ thread: first.id }, { replace: true });
  }, [selectedId, threads, setSearchParams]);

  const { data: thread } = useQuery({
    queryKey: ['admin-message-thread', selectedId],
    queryFn: () => adminMessageService.getThread(selectedId as string),
    enabled: Boolean(selectedId),
    refetchInterval: POLL_MS,
  });

  // Opening a thread is reading it.
  useEffect(() => {
    if (!selectedId || !thread?.unreadCount) return;
    adminMessageService
      .markRead(selectedId)
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['admin-message-threads'] });
        queryClient.invalidateQueries({ queryKey: ['admin-message-thread', selectedId] });
      })
      .catch(() => {
        /* a stuck badge is not worth an error state */
      });
  }, [selectedId, thread?.unreadCount, queryClient]);

  const reply = useMutation({
    mutationFn: (body: string) => adminMessageService.reply(selectedId as string, body),
    onSuccess: () => {
      setDraft('');
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['admin-message-threads'] });
      queryClient.invalidateQueries({ queryKey: ['admin-message-thread', selectedId] });
    },
    onError: (err: unknown) => {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'Could not send that reply.'
      );
    },
  });

  const canSend = Boolean(selectedId) && draft.trim().length > 0 && !reply.isPending;

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Messages</h1>
          <p className="mt-1 text-gray-400">
            {inbox
              ? `${inbox.total} conversation${inbox.total === 1 ? '' : 's'}${
                  inbox.unreadTotal ? ` · ${inbox.unreadTotal} unread` : ''
                }`
              : 'Loading…'}
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        {/* Threads */}
        <div className="max-h-[70vh] overflow-y-auto rounded-xl border border-gray-700 bg-gray-800">
          {isLoading ? (
            <div className="p-8 text-center text-gray-400">Loading…</div>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <MessageSquare className="mx-auto mb-3 text-gray-600" size={28} />
              No one has written in yet.
            </div>
          ) : (
            threads.map((item) => (
              <ThreadRow
                key={item.id}
                thread={item}
                active={item.id === selectedId}
                onSelect={() => {
                  setSearchParams({ thread: item.id });
                  setDraft('');
                  setError(null);
                }}
              />
            ))
          )}
        </div>

        {/* Conversation */}
        <div className="flex min-h-[70vh] flex-col rounded-xl border border-gray-700 bg-gray-800">
          {!thread ? (
            <div className="flex flex-1 items-center justify-center text-gray-400">
              Pick a conversation
            </div>
          ) : (
            <>
              <div className="border-b border-gray-700 p-5">
                <h2 className="text-lg font-semibold text-white">
                  {thread.businessName || thread.user.name}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-gray-400">
                  <a
                    href={`mailto:${thread.user.email}`}
                    className="flex items-center gap-1.5 hover:text-emerald-400"
                  >
                    <Mail size={14} />
                    {thread.user.email}
                  </a>
                  {thread.websiteDomain && <span>{thread.websiteDomain}</span>}
                </div>
                {thread.deliveryError && (
                  <p className="mt-3 flex items-start gap-2 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-300">
                    <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                    <span>
                      Their last message never reached your inbox — {thread.deliveryError}. Check
                      SMTP config; the message itself is safe here.
                    </span>
                  </p>
                )}
              </div>

              <ul className="flex-1 space-y-3 overflow-y-auto p-5">
                {thread.messages.map((message) => (
                  <MessageBubble key={message.id} message={message} />
                ))}
              </ul>

              <form
                className="border-t border-gray-700 p-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canSend) reply.mutate(draft.trim());
                }}
              >
                {error && <p className="mb-2 text-sm text-red-400">{error}</p>}
                <div className="flex gap-3">
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={2}
                    maxLength={4000}
                    placeholder={`Reply to ${thread.user.name}…`}
                    className="flex-1 resize-none rounded-lg border border-gray-700 bg-gray-900 px-3 py-2 text-white placeholder-gray-500 outline-none focus:border-emerald-500"
                  />
                  <button
                    type="submit"
                    disabled={!canSend}
                    className="flex items-center gap-2 self-end rounded-lg bg-emerald-500 px-5 py-2.5 font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Send size={16} />
                    {reply.isPending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Goes into their portal thread, their notification bell, and their email.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
